// Hectáreas por producto de cada mapeo de maquinaria para una empresa.
// Cada producto (p. ej. Herbicidas · Dron · Tasa variada) tiene sus propias
// hectáreas compradas; los análisis que terminan bien descuentan su área del
// producto que usaron. El control se enciende por mapeo.
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import {
    Box,
    Typography,
    Switch,
    FormControlLabel,
    TextField,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    LinearProgress,
    Chip,
    Button,
    Stack,
    Collapse,
} from '@mui/material';
import { History, Save } from '@mui/icons-material';
import { API_BASE_URL_SECURITY } from '../../../../utils/config';
import { PRODUCTOS_CREDITOS, TIPOS_CREDITOS, formatHa } from '../../../../utils/creditosAps';

const NIVEL = {
    ok: { label: 'Con saldo', color: 'success' },
    bajo: { label: 'Saldo bajo', color: 'warning' },
    agotado: { label: 'Agotado', color: 'error' },
    sin_asignar: { label: 'No contratado', color: 'default' },
};

const ESTADO_MOVIMIENTO = {
    consumido: 'Descontado',
    pendiente: 'En proceso',
    fallido: 'Falló (sin descuento)',
    vencido: 'No terminó (sin descuento)',
    aplicado: 'Asignación',
};

const labelProducto = (clave) => PRODUCTOS_CREDITOS.find((p) => p.producto === clave)?.label || clave;
const labelTipo = (tipo) => TIPOS_CREDITOS.find((t) => t.tipo === tipo)?.label || tipo;

// Security anterior solo devolvía `controlActivo` (= Aplicaciones Aéreas).
const tiposActivosDe = (resumen) => {
    if (Array.isArray(resumen?.tiposActivos)) return resumen.tiposActivos;
    return resumen?.controlActivo ? ['APLICACIONES_AEREAS'] : [];
};

const CompanyAerialCredits = ({ empresa, securityConfig, onSaved }) => {
    const resumen = empresa?.creditosAps;
    const [tiposActivos, setTiposActivos] = useState([]);
    const [valores, setValores] = useState({});
    const [guardando, setGuardando] = useState(false);
    const [verHistorial, setVerHistorial] = useState(false);
    const [movimientos, setMovimientos] = useState([]);

    useEffect(() => {
        setTiposActivos(tiposActivosDe(resumen));
        setValores(Object.fromEntries((resumen?.productos || []).map((p) => [p.producto, String(p.hectareasAsignadas || 0)])));
    }, [empresa?._id, resumen]);

    const cargarMovimientos = useCallback(async () => {
        if (!empresa?._id) return;
        try {
            const { data } = await axios.get(
                `${API_BASE_URL_SECURITY}empresas/${empresa._id}/creditos-aps/movimientos`,
                securityConfig()
            );
            setMovimientos(Array.isArray(data) ? data : []);
        } catch (error) {
            toast.error(error.response?.data?.message || 'No fue posible cargar el historial.');
        }
    }, [empresa?._id, securityConfig]);

    useEffect(() => {
        if (verHistorial) cargarMovimientos();
    }, [verHistorial, cargarMovimientos]);

    if (!empresa) return null;

    const guardar = async () => {
        const productos = PRODUCTOS_CREDITOS.map(({ producto }) => ({
            producto,
            hectareasAsignadas: Number(valores[producto] || 0),
        }));
        if (productos.some((p) => !Number.isFinite(p.hectareasAsignadas) || p.hectareasAsignadas < 0)) {
            toast.error('Las hectáreas deben ser números mayores o iguales a cero.');
            return;
        }
        setGuardando(true);
        try {
            await axios.put(
                `${API_BASE_URL_SECURITY}empresas/${empresa._id}/creditos-aps`,
                { activo: tiposActivos.length > 0, tiposActivos, productos },
                securityConfig()
            );
            toast.success('Hectáreas por producto guardadas.');
            if (typeof onSaved === 'function') await onSaved();
            if (verHistorial) await cargarMovimientos();
        } catch (error) {
            toast.error(error.response?.data?.message || 'No fue posible guardar las hectáreas por producto.');
        } finally {
            setGuardando(false);
        }
    };

    return (
        <Box sx={{ p: 2, borderRadius: 2, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
            <Typography variant="subtitle2" fontWeight={700}>
                Hectáreas por producto
            </Typography>
            <Typography variant="caption" color="text.secondary">
                Cada análisis que termina bien descuenta su área del producto usado. Con saldo en cero ya no se puede analizar ese producto.
                Los mapeos sin control trabajan como hasta ahora.
            </Typography>

            {TIPOS_CREDITOS.map(({ tipo, label: tituloTipo }) => {
                const activo = tiposActivos.includes(tipo);
                return (
                    <Box key={tipo} sx={{ mt: 2, pt: 1.5, borderTop: '1px solid #e2e8f0' }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
                            <Typography variant="body1" fontWeight={700}>{tituloTipo}</Typography>
                            <FormControlLabel
                                control={(
                                    <Switch
                                        checked={activo}
                                        onChange={(e) => setTiposActivos((prev) => (e.target.checked
                                            ? [...new Set([...prev, tipo])]
                                            : prev.filter((t) => t !== tipo)))}
                                    />
                                )}
                                label={activo ? 'Control activo' : 'Sin control (acceso como hasta ahora)'}
                            />
                        </Stack>

                        <Table size="small" sx={{ mt: 0.5, opacity: activo ? 1 : 0.6 }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Producto</TableCell>
                                    <TableCell sx={{ width: 140 }}>Compradas (ha)</TableCell>
                                    <TableCell>Consumo</TableCell>
                                    <TableCell align="right">Disponibles</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {PRODUCTOS_CREDITOS.filter((def) => def.tipo === tipo).map(({ producto, nombre }) => {
                                    const p = (resumen?.productos || []).find((x) => x.producto === producto) || {};
                                    const nivel = NIVEL[p.nivel] || NIVEL.sin_asignar;
                                    return (
                                        <TableRow key={producto}>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight={600}>{nombre}</Typography>
                                                <Chip size="small" label={nivel.label} color={nivel.color} sx={{ mt: 0.5 }} />
                                            </TableCell>
                                            <TableCell>
                                                <TextField
                                                    size="small"
                                                    type="number"
                                                    inputProps={{ min: 0, step: '0.01' }}
                                                    value={valores[producto] ?? '0'}
                                                    onChange={(e) => setValores((prev) => ({ ...prev, [producto]: e.target.value }))}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ minWidth: 140 }}>
                                                <LinearProgress
                                                    variant="determinate"
                                                    value={Number(p.porcentajeUtilizado || 0)}
                                                    color={nivel.color === 'default' ? 'primary' : nivel.color}
                                                    sx={{ height: 7, borderRadius: 5, mb: 0.5 }}
                                                />
                                                <Typography variant="caption" color="text.secondary">
                                                    {formatHa(p.hectareasConsumidas)} usadas
                                                    {Number(p.hectareasExcedidas) > 0 ? ` · ${formatHa(p.hectareasExcedidas)} sobre lo comprado` : ''}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Typography variant="body2" fontWeight={700}>{formatHa(p.hectareasDisponibles)}</Typography>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </Box>
                );
            })}

            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                Para recargar, escriba el nuevo total comprado (lo ya consumido se conserva). El aviso de saldo bajo sale al quedar el {resumen?.umbralSaldoBajoPct ?? 20} % o menos.
            </Typography>

            <Stack direction="row" spacing={1.5} sx={{ mt: 1.5 }}>
                <Button variant="contained" startIcon={<Save />} onClick={guardar} disabled={guardando}
                        sx={{
                            backgroundColor: '#111827',
                            color: '#fff',
                            fontWeight: 700,
                            '&:hover': { backgroundColor: '#000', color: '#fff' },
                            '&.Mui-disabled': { backgroundColor: '#6b7280', color: '#f3f4f6' },
                        }}>
                    {guardando ? 'Guardando…' : 'Guardar hectáreas'}
                </Button>
                <Button variant="outlined" startIcon={<History />} onClick={() => setVerHistorial((v) => !v)}>
                    {verHistorial ? 'Ocultar historial' : 'Ver historial de consumo'}
                </Button>
            </Stack>

            <Collapse in={verHistorial}>
                <Box sx={{ mt: 1.5, maxHeight: 260, overflowY: 'auto', bgcolor: '#fff', border: '1px solid #e5e7eb', borderRadius: 1 }}>
                    {movimientos.length === 0 && (
                        <Typography variant="body2" color="text.secondary" sx={{ p: 1.5 }}>Sin movimientos todavía.</Typography>
                    )}
                    {movimientos.map((m) => (
                        <Box key={m._id} sx={{ px: 1.5, py: 1, borderBottom: '1px solid #f1f5f9' }}>
                            <Typography variant="body2" fontWeight={600}>
                                {m.tipo === 'asignacion'
                                    ? `Asignación${m.activo === false ? ' (control desactivado)' : ''}${Array.isArray(m.tiposActivos) && m.tiposActivos.length ? ` · control en ${m.tiposActivos.map(labelTipo).join(', ')}` : ''}`
                                    : `${labelProducto(m.producto)} · ${m.estado === 'consumido' ? `−${formatHa(m.hectareas)}` : ESTADO_MOVIMIENTO[m.estado] || m.estado}`}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {new Date(m.createdAt).toLocaleString('es-GT')}
                                {m.tipo === 'asignacion'
                                    ? ` · ${(m.cambios || []).map((c) => `${labelProducto(c.producto)}: ${formatHa(c.antes)} → ${formatHa(c.despues)}`).join(' · ') || 'sin cambios de hectáreas'}`
                                    : ` · Análisis ${m.idAnalisis}${m.usuarioEmail ? ` · ${m.usuarioEmail}` : ''}${m.saldoDespues !== undefined ? ` · saldo ${formatHa(m.saldoDespues)}` : ''}`}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            </Collapse>
        </Box>
    );
};

export default CompanyAerialCredits;
